import { useState } from 'react';
import { Fingerprint, Loader2, X } from 'lucide-react';
import PinPad from '@/components/desktop/PinPad';
import { getElectronAPI } from '@/desktop/electronApi';
import { useAuthStore } from '@/store/authStore';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import {
  canUsePlatformBiometrics,
  enrollPlatformFingerprint,
} from '@/desktop/fingerprintWebAuthn';
import { ensureDeviceId as ensureOfflineDeviceId } from '@/offline';

interface Props {
  open: boolean;
  onClose: () => void;
  onEnrolled?: () => void;
  /** Require online for wipe / admin — enrollment itself works offline after cloud bind */
  title?: string;
  /** During setup / reset PIN — no skip via close button */
  required?: boolean;
}

export default function EnrollPinModal({ open, onClose, onEnrolled, title, required }: Props) {
  const api = getElectronAPI();
  const { user, company, role, canUsePOS } = useAuthStore();
  const applyUnlock = useDesktopSessionStore((s) => s.applyUnlock);
  const setPendingEnroll = useDesktopSessionStore((s) => s.setPendingEnrollAfterLogin);
  const setResetPin = useDesktopSessionStore((s) => s.setResetPinAfterLogin);

  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [step, setStep] = useState<'pin' | 'confirm' | 'fingerprint'>('pin');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [localId, setLocalId] = useState<string | null>(null);

  if (!open) return null;

  const enroll = async () => {
    if (!api || !user || !company) {
      setError('Not signed in');
      return;
    }
    if (pin.length !== 4) {
      setError('PIN must be 4 digits');
      return;
    }
    if (pin !== confirm) {
      setError('PINs do not match');
      setConfirm('');
      setStep('confirm');
      return;
    }
    setBusy(true);
    setError('');
    try {
      let deviceId: string | undefined;
      try {
        deviceId = await ensureOfflineDeviceId();
      } catch {
        deviceId = undefined;
      }
      const profile = await api.vault.enrollCashier({
        serverUserId: user.name || user.full_name || 'user',
        displayName: user.full_name || user.name || 'Cashier',
        role,
        companyCode: company.code,
        companyName: company.name,
        canUsePOS,
        pin,
        deviceId,
        userSnapshot: {
          user,
          company,
          role,
          can_use_pos: canUsePOS,
        },
      });
      setLocalId(profile.localId);
      applyUnlock({
        profile,
        userSnapshot: {
          user,
          company,
          role,
          can_use_pos: canUsePOS,
        },
        cloudApplied: true,
      });
      useDesktopSessionStore.getState().setCloudSessionExpired(false);
      setPendingEnroll(false);
      setResetPin(false);
      if (canUsePlatformBiometrics()) {
        setStep('fingerprint');
      } else {
        onEnrolled?.();
        onClose();
      }
    } catch (err) {
      setError((err as Error)?.message || 'Enrollment failed');
    } finally {
      setBusy(false);
    }
  };

  const enrollFp = async () => {
    if (!api || !localId || !user) return;
    setBusy(true);
    setError('');
    try {
      const credId = await enrollPlatformFingerprint(
        localId,
        user.full_name || user.name || 'Cashier',
      );
      await api.vault.setFingerprint(localId, credId);
      onEnrolled?.();
      onClose();
    } catch (err) {
      setError((err as Error)?.message || 'Fingerprint enrollment failed');
    } finally {
      setBusy(false);
    }
  };

  const finishSkipFp = () => {
    onEnrolled?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{title || 'Enroll desktop unlock PIN'}</h3>
          {!required && (
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          )}
        </div>
        <div className="p-4 space-y-4">
          {step === 'pin' && (
            <>
              <p className="text-sm text-gray-600">Choose a 4 digit PIN for offline unlock on this PC.</p>
              <PinPad
                value={pin}
                onChange={(v) => {
                  setPin(v);
                  if (v.length === 4) setStep('confirm');
                }}
                maxLength={4}
              />
            </>
          )}
          {step === 'confirm' && (
            <>
              <p className="text-sm text-gray-600">Confirm your PIN.</p>
              <PinPad value={confirm} onChange={setConfirm} maxLength={4} disabled={busy} />
              <button
                type="button"
                disabled={busy || confirm.length !== 4}
                onClick={() => void enroll()}
                className="btn-primary w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save PIN
              </button>
              <button type="button" className="text-xs text-gray-500 w-full" onClick={() => { setStep('pin'); setConfirm(''); setPin(''); }}>
                Back
              </button>
            </>
          )}
          {step === 'fingerprint' && (
            <>
              <p className="text-sm text-gray-600">Optional: enroll fingerprint for faster unlock.</p>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="button"
                disabled={busy}
                onClick={() => void enrollFp()}
                className="btn-primary w-full py-2.5 flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                Enroll fingerprint
              </button>
              <button type="button" className="w-full text-sm text-gray-600 py-2" onClick={finishSkipFp}>
                Skip for now
              </button>
            </>
          )}
          {step !== 'fingerprint' && error && <p className="text-sm text-red-600 text-center">{error}</p>}
        </div>
      </div>
    </div>
  );
}
